<template>
  <div
    ref="rootRef"
    class="plugin-root-viewport flex flex-col bg-[var(--plugin-bg)]"
    :class="{ dark: isDarkTheme }"
    :data-theme="pluginThemePreset ?? undefined"
    :style="pluginSettingsStyle"
  >
    <!-- Inner wrapper: padding lives here so root size never exceeds viewport -->
    <div class="plugin-root-inner flex min-h-0 flex-1 flex-col pt-6 lg:pt-8">
      <!-- Content block: fit viewport, no page scroll; full width on mobile/iframe, max 80vw on desktop -->
      <div class="mx-auto flex w-full min-h-0 min-w-0 max-w-full flex-1 flex-col items-stretch gap-0 overflow-hidden lg:max-w-[80vw] lg:flex-row lg:justify-center lg:gap-8">
      <!-- Left column: full width on mobile, max 45rem; shrinks in narrow iframes -->
      <div class="mx-auto flex min-h-0 min-w-0 w-full max-w-[45rem] flex-1 flex-col lg:mx-0">
        <template v-if="notFound">
          <div class="rounded-[20px] bg-card py-12 text-center">
            <h1 class="text-xl font-medium text-[var(--plugin-charcoal)]">404</h1>
            <p class="mt-2 text-[var(--plugin-gray-500)]">{{ t('plugin.error.load') }}</p>
          </div>
        </template>
        <template v-else-if="loading">
          <div class="rounded-[20px] bg-card py-12 text-center text-[var(--plugin-gray-500)]">
            {{ t('plugin.loading') }}
          </div>
        </template>
        <template v-else>
          <!-- Header: fixed at top -->
          <header class="flex h-12 shrink-0 items-center justify-center">
            <span
              class="inline-block h-6 w-auto max-w-full [&>svg]:h-full [&>svg]:w-auto [&>svg]:object-contain"
              aria-label="infinela - rezervačný systém"
              v-html="logoSvgRaw"
            />
          </header>
          <div class="h-2 shrink-0" />

          <!-- Mobile stepper (matches Flutter CustomMobileStepper) -->
          <div v-if="!params.readonly" class="mb-4 shrink-0 lg:hidden">
            <MobileStepper :all-steps-completed="isPaymentReturn" />
          </div>

          <!-- Step content: only this area scrolls when content is tall (Flutter: Expanded(SingleChildScrollView)) -->
          <main class="scroll-touch min-h-0 flex-1 overflow-y-auto overflow-x-hidden rounded-[20px] px-3 pt-4 pb-4 lg:px-6 lg:pt-5 lg:pb-5">
            <PaymentStatusStep
              v-if="isPaymentReturn && params.reservationId != null"
              :reservation-id="params.reservationId"
            />
            <BranchesStep v-else-if="state.step === 'branches'" :facility-id="facilityId!" />
            <ProductsStep
              v-else-if="state.step === 'products'"
              :branch-id="state.branch!.id"
              :source="params.source ?? undefined"
            />
            <DateTimeStep
              v-else-if="state.step === 'datetime'"
              :branch-id="state.branch!.id"
              :product="state.product"
              :source="params.source ?? undefined"
            />
            <EmployeesStep
              v-else-if="state.step === 'employees'"
              :branch-id="state.branch!.id"
              :product="state.product"
              :selected-date="state.selectedDate"
              :selected-time="state.selectedTime"
            />
            <CustomerStep v-else-if="state.step === 'customer'" />
            <SummaryStep
              v-else-if="state.step === 'summary' && !isPaymentReturn"
              :payment-methods="paymentMethods"
              :enabled-payment-method-ids="pluginSettings?.enabled_payment_methods ?? []"
              :required-payment="!!pluginSettings?.required_payment"
              :facility-name="facilityName"
              :facility-slug="effectiveFacilitySlug"
              :is-zlava-dna="params.source === 'zlavadna'"
              :submit-error="submitError"
            />
            <PaymentStep
              v-else-if="state.step === 'payment'"
              :submitting="submitting"
              :submit-error="submitError"
            />
          </main>

          <!-- Footer: fixed at bottom with padding (match Flutter: buttons + copyWith(bottom: horizontalPadding)) -->
          <footer v-if="!params.readonly" class="mt-2 flex w-full shrink-0 gap-3 pb-5 lg:mt-2 lg:gap-4 lg:pb-8">
            <button
              v-if="isPaymentReturn"
              type="button"
              class="h-12 flex-1 rounded-[8px] bg-primary px-4 text-sm font-medium text-[var(--plugin-on-primary)] hover:bg-[var(--plugin-primary-hover)] w-full"
              @click="goCreateAnotherReservation"
            >
              {{ t('plugin.buttons.createAnotherReservation') }}
            </button>
            <button
              v-else-if="canGoBack && !state.submitted"
              type="button"
              class="h-12 flex-1 rounded-[8px] border border-[var(--plugin-border)] bg-card px-4 text-sm font-medium text-[var(--plugin-on-bg)] hover:bg-[var(--plugin-gray-100)]"
              @click="goBack"
            >
              {{ t('plugin.actions.back') }}
            </button>
            <button
              v-if="state.step === 'summary' && state.submitted"
              type="button"
              class="h-12 flex-1 rounded-[8px] bg-primary px-4 text-sm font-medium text-[var(--plugin-on-primary)] hover:bg-[var(--plugin-primary-hover)] w-full"
              @click="submitError = null; resetWizard()"
            >
              {{ t('plugin.buttons.nextReservation') }}
            </button>
            <button
              v-else-if="!isPaymentReturn && canGoNext && state.step !== 'summary' && state.step !== 'payment'"
              type="button"
              class="h-12 flex-1 rounded-[8px] bg-primary px-4 text-sm font-medium text-[var(--plugin-on-primary)] hover:bg-[var(--plugin-primary-hover)] disabled:opacity-50"
              :class="{ 'w-full': !canGoBack }"
              :disabled="!isStepComplete || state.isCouponVerifying"
              @click="goNext"
            >
              {{ t('plugin.actions.next') }}
            </button>
            <button
              v-if="state.step === 'summary' && !state.submitted"
              type="button"
              class="h-12 flex-1 rounded-[8px] bg-primary px-4 text-sm font-medium text-[var(--plugin-on-primary)] hover:bg-[var(--plugin-primary-hover)] disabled:opacity-50"
              :class="{ 'w-full': !canGoBack }"
              :disabled="!canConfirmReservation"
              @click="onConfirmReservation"
            >
              {{ t('plugin.buttons.confirmReservation') }}
            </button>
            <button
              v-if="state.step === 'payment' && !state.submitted"
              type="button"
              class="h-12 flex-1 rounded-[8px] bg-primary px-4 text-sm font-medium text-[var(--plugin-on-primary)] hover:bg-[var(--plugin-primary-hover)] disabled:opacity-50"
              :class="{ 'w-full': !canGoBack }"
              :disabled="submitting"
              @click="submitReservation"
            >
              {{ t('plugin.actions.submit') }}
            </button>
          </footer>
        </template>
      </div>

      <!-- Right column: vertical stepper (desktop only), same height as left column -->
      <aside v-if="!notFound && !loading && !params.readonly" class="hidden min-h-0 min-w-0 shrink lg:flex lg:h-full lg:flex-col lg:self-stretch">
        <DesktopStepper :all-steps-completed="isPaymentReturn" />
      </aside>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import logoSvgRaw from '@/assets/horizontal-logo-plugin.svg?raw'
import {
  getFacility,
  getFacilitySlugByBranch,
  getFacilitySlugByService,
  getPluginSettings,
  getPaymentMethods,
  createReservation,
  initiatePaymentWithReservation,
  getServicesPackages,
  resolveZlavaDnaService,
  verifyZlavaDnaCoupon,
} from '@/api/public'
import { usePluginParams } from '@/composables/usePluginParams'
import { useResizeNotifier } from '@/composables/useResizeNotifier'
import {
  useWizardState,
  setWizardStep,
  setBranch,
  setProduct,
  setCustomer,
  setReservationId,
  setSubmitted,
  resetWizard,
  setSelectedPaymentMethodId,
  setZdDealItemId,
  setCouponVerification,
  setExternalPricing,
} from '@/composables/useWizardState'
import type { PaymentMethodItem } from '@/api/public'
import { getBranch } from '@/api/public'
import { t } from '@/i18n'
import { isCardPaymentName } from '@/utils/paymentMethods'
import BranchesStep from '@/components/steps/BranchesStep.vue'
import ProductsStep from '@/components/steps/ProductsStep.vue'
import DateTimeStep from '@/components/steps/DateTimeStep.vue'
import EmployeesStep from '@/components/steps/EmployeesStep.vue'
import CustomerStep from '@/components/steps/CustomerStep.vue'
import SummaryStep from '@/components/steps/SummaryStep.vue'
import PaymentStep from '@/components/steps/PaymentStep.vue'
import PaymentStatusStep from '@/components/steps/PaymentStatusStep.vue'
import DesktopStepper from '@/components/DesktopStepper.vue'
import MobileStepper from '@/components/MobileStepper.vue'
import type { WizardStep } from '@/composables/useWizardState'
import type { PluginSettings } from '@/types/api'
import { isValidEmail, isValidPhone } from '@/utils/validators'
import { getReservationErrorKey } from '@/utils/reservationError'
import { readableOn, mixColors } from '@/utils/colorContrast'

const router = useRouter()
const params = usePluginParams()
const state = useWizardState()
const rootRef = ref<HTMLElement | null>(null)

const isPaymentReturn = computed(() => params.value.reservationId != null)
useResizeNotifier(rootRef)

const facilityId = ref<number | null>(null)
const facilityName = ref<string>('')
const pluginSettings = ref<PluginSettings | null>(null)
const paymentMethods = ref<PaymentMethodItem[]>([])
const loading = ref(true)
const notFound = ref(false)
/** When on root URL, slug resolved from service_id/branch_id (no redirect). Used e.g. for "Create another". */
const resolvedSlugRef = ref<string>('')
/** Slug currently in use — prefers URL param, falls back to resolved one. */
const effectiveFacilitySlug = computed(
  () =>
    params.value.facilitySlug?.trim() ??
    params.value.facilitySlugFromQuery?.trim() ??
    resolvedSlugRef.value ??
    ''
)
const submitting = ref(false)
const submitError = ref<string | null>(null)

const externalCssLink = ref<HTMLLinkElement | null>(null)
const stepOrder: WizardStep[] = [
  'branches',
  'products',
  'datetime',
  'employees',
  'customer',
  'summary',
  'payment',
]

const pluginThemePreset = computed(() => {
  const t = params.value.theme
  return t === 'zlavadna' || t === 'zlavadna-dark' ? t : null
})
const isDarkTheme = computed(() => params.value.theme === 'zlavadna-dark')

function fontFamilyStack(fontFamily: string | undefined): string {
  if (!fontFamily || !fontFamily.trim()) return 'Outfit, system-ui, sans-serif'
  const name = fontFamily.trim().toLowerCase()
  if (name === 'outfit') return 'Outfit, system-ui, sans-serif'
  return `${fontFamily}, system-ui, sans-serif`
}

const pluginSettingsStyle = computed(() => {
  if (pluginThemePreset.value) return undefined
  const s = pluginSettings.value
  if (!s) return undefined
  const bg = s.background_color ?? '#ffffff'
  const text = s.text_color ?? '#12181f'
  const primary = s.button_color ?? '#f7522c'
  const radius = s.border_radius != null ? `${s.border_radius}px` : '8px'
  const onBg = readableOn(bg)
  const onPrimary = readableOn(primary)
  const cardBg = mixColors(bg, onBg, 0.06)
  const surfaceBg = mixColors(bg, onBg, 0.10)
  const border = mixColors(bg, onBg, 0.18)
  const muted = mixColors(bg, onBg, 0.55)
  const subtle = mixColors(bg, onBg, 0.40)
  const track = mixColors(bg, onBg, 0.14)
  const primaryHover = mixColors(primary, onPrimary, 0.15)
  return {
    '--plugin-bg': bg,
    '--plugin-card-bg': cardBg,
    '--plugin-text': text,
    '--plugin-charcoal': text,
    '--plugin-on-bg': onBg,
    '--plugin-on-primary': onPrimary,
    '--plugin-primary': primary,
    '--plugin-primary-hover': primaryHover,
    '--plugin-border': border,
    '--plugin-gray-100': surfaceBg,
    '--plugin-gray-400': muted,
    '--plugin-gray-500': subtle,
    '--plugin-progress-track': track,
    '--plugin-radius': radius,
    '--plugin-card-radius': radius,
    fontFamily: fontFamilyStack(s.font_family),
  } as Record<string, string>
})

const isWorkshop = computed(
  () =>
    state.product?.type === 'workshop' ||
    (state.product as { is_workshop?: boolean } | null)?.is_workshop === true
)

const isEmployeeSelectable = computed(
  () => state.product?.is_employee_selectable !== false
)

const visibleStepOrder = computed(() => {
  if (isWorkshop.value) {
    return stepOrder.filter(
      (s) => s !== 'datetime' && s !== 'employees'
    )
  }
  if (!isEmployeeSelectable.value) {
    return stepOrder.filter((s) => s !== 'employees')
  }
  return stepOrder
})

const currentStepIndex = computed(() => visibleStepOrder.value.indexOf(state.step))
const canGoBack = computed(() => currentStepIndex.value > 0)
const canGoNext = computed(
  () =>
    currentStepIndex.value < visibleStepOrder.value.length - 1 &&
    currentStepIndex.value >= 0
)

const isStepComplete = computed(() => {
  switch (state.step) {
    case 'branches':
      return !!state.branch
    case 'products':
      return !!state.product
    case 'datetime':
      return !!state.selectedDate && !!state.selectedTime && !!state.selectedSlot
    case 'employees':
      return true
    case 'customer': {
      const base =
        !!(
          state.customer.name?.trim() &&
          state.customer.surname?.trim() &&
          state.customer.email?.trim() &&
          state.customer.phone?.trim() &&
          isValidEmail(state.customer.email) &&
          isValidPhone(state.customer.phone)
        )
      if (params.value.source === 'zlavadna') {
        return (
          base &&
          !!state.customer.couponCode?.trim() &&
          !!state.customer.couponPassword?.trim()
        )
      }
      return base
    }
    case 'summary':
    case 'payment':
      return true
    default:
      return false
  }
})

const isZlavaDna = computed(() => params.value.source === 'zlavadna')
const requiresPayment = computed(
  () => !isZlavaDna.value && !!(pluginSettings.value?.required_payment)
)
const hasPaymentOptions = computed(() => {
  const methods = paymentMethods.value
  const enabledIds = pluginSettings.value?.enabled_payment_methods ?? []
  if (methods.length === 0) return false
  if (enabledIds.length > 0) return methods.some((m) => enabledIds.includes(m.id))
  return true
})
const canConfirmReservation = computed(() => {
  if (!state.termsAgreement || !state.privacyAgreement) return false
  if (hasPaymentOptions.value && state.selectedPaymentMethodId == null) return false
  return true
})

const selectedPaymentMethod = computed(() =>
  paymentMethods.value.find((m) => m.id === state.selectedPaymentMethodId) ?? null
)

const selectedPaymentMethodIsCard = computed(() => {
  const method = selectedPaymentMethod.value
  if (!method) return false
  return isCardPaymentName(method.name)
})

function goBack() {
  const idx = currentStepIndex.value
  if (idx > 0) setWizardStep(visibleStepOrder.value[idx - 1])
}

async function goNext() {
  const idx = currentStepIndex.value
  const order = visibleStepOrder.value
  if (idx < 0 || idx >= order.length - 1) return
  if (state.step === 'customer' && isZlavaDna.value) {
    const branch = state.branch
    const product = state.product
    const zdId = state.zdDealItemId
    const code = state.customer.couponCode?.trim()
    const password = state.customer.couponPassword?.trim()
    if (!branch || !product || !zdId || !code || !password) return
    setCouponVerification(null, null, true)
    try {
      await verifyZlavaDnaCoupon({
        branch_id: branch.id,
        service_id: product.type === 'service' ? product.id : undefined,
        package_id: product.type === 'package' ? product.id : undefined,
        coupon_code: code,
        coupon_password: password,
        zd_deal_item_id: zdId,
      })
      setCouponVerification(true, null, false)
      setWizardStep('summary')
    } catch (e) {
      let msg = t('plugin.customerStep.couponVerificationFailed')
      const errMsg = e instanceof Error ? e.message : String(e)
      const jsonMatch = errMsg.match(/^API \d+: (.+)$/s)
      if (jsonMatch?.[1]) {
        try {
          const parsed = JSON.parse(jsonMatch[1]) as { message?: string }
          if (parsed?.message) msg = parsed.message
        } catch {
          if (errMsg.length < 200) msg = errMsg
        }
      } else if (errMsg.length < 200) {
        msg = errMsg
      }
      setCouponVerification(false, msg, false)
    }
    return
  }
  setWizardStep(order[idx + 1])
}

function goCreateAnotherReservation() {
  const initialUrl = params.value.initialUrl
  const facilitySlug =
    params.value.facilitySlug?.trim() ??
    params.value.facilitySlugFromQuery?.trim() ??
    resolvedSlugRef.value ??
    ''
  if (initialUrl) {
    try {
      const url = new URL(initialUrl, window.location.origin)
      const isSameOriginPath = url.origin === window.location.origin && initialUrl.startsWith('/') && !initialUrl.startsWith('//')
      if (isSameOriginPath) {
        const query: Record<string, string> = {}
        url.searchParams.forEach((v, k) => {
          if (k !== 'reservation_id' && k !== 'initial_url') query[k] = v
        })
        router.push({ path: url.pathname, query }).then(() => {
          resetWizard()
          setWizardStep('branches')
        }).catch(() => { /* navigation aborted */ })
        return
      }
    } catch {
      // fallback to facility slug
    }
    window.location.href = initialUrl
    return
  }
  if (facilitySlug) {
    router.push({ name: 'plugin', params: { facilitySlug }, query: {} }).then(() => {
      resetWizard()
      setWizardStep('branches')
    }).catch(() => { /* navigation aborted */ })
  } else if (resolvedSlugRef.value) {
    resetWizard()
    setWizardStep('branches')
  }
}

function onConfirmReservation() {
  if (!state.termsAgreement) {
    submitError.value = t('plugin.summary.errorAgreeTerms')
    return
  }
  if (!state.privacyAgreement) {
    submitError.value = t('plugin.summary.errorAgreePrivacy')
    return
  }
  if (hasPaymentOptions.value && state.selectedPaymentMethodId == null) {
    submitError.value = t('plugin.summary.paymentMethodRequired')
    return
  }
  submitError.value = null
  void submitReservation()
}

async function submitReservation() {
  const branch = state.branch
  const product = state.product
  const customer = state.customer
  if (!branch || !product || !state.selectedDate || !state.selectedSlot) return
  if (hasPaymentOptions.value && state.selectedPaymentMethodId == null) {
    submitError.value = t('plugin.summary.paymentMethodRequired')
    return
  }
  submitting.value = true
  submitError.value = null
  try {
    let reservationDatetime: string
    if ((product.type === 'workshop' || product.is_workshop) && product.workshop_datetime) {
      reservationDatetime = product.workshop_datetime
    } else if (state.selectedSlot?.start) {
      reservationDatetime = state.selectedSlot.start
    } else {
      const time = state.selectedTime ?? ''
      reservationDatetime = `${state.selectedDate}T${time.length >= 5 ? time.slice(0, 5) : time}:00`
    }
    const enabledIds = pluginSettings.value?.enabled_payment_methods ?? []
    const methods = paymentMethods.value
    const paymentMethodId =
      hasPaymentOptions.value
        ? (state.selectedPaymentMethodId ?? null)
        : state.selectedPaymentMethodId ??
          methods.find((m) => enabledIds.length === 0 || enabledIds.includes(m.id))?.id ??
          methods[0]?.id
    if (paymentMethodId == null) throw new Error('No payment method available')
    const baseReservationData = {
      branch_id: branch.id,
      reservation_datetime: reservationDatetime,
      service_id: product.type === 'service' ? product.id : undefined,
      package_id: product.type === 'package' ? product.id : undefined,
      workshop_id: product.type === 'workshop' || product.is_workshop ? product.id : undefined,
      employee_ids: state.employee && isEmployeeSelectable.value ? [state.employee.id] : undefined,
      customer: {
        name: customer.name,
        surname: customer.surname,
        email: customer.email,
        phone: customer.phone,
      },
      number_of_people: product.type === 'workshop' || product.is_workshop ? state.customer.numberOfPeople : undefined,
      note: customer.note || undefined,
      source: params.value.source ?? 'plugin',
      payment_method_id: paymentMethodId,
      ...(params.value.source === 'zlavadna'
        ? {
            coupon_code: state.customer.couponCode?.trim() || undefined,
            coupon_password: state.customer.couponPassword?.trim() || undefined,
            zd_deal_item_id: state.zdDealItemId ?? undefined,
          }
        : {}),
    }

    if (!isZlavaDna.value && (requiresPayment.value || selectedPaymentMethodIsCard.value)) {
      const initialUrl =
        typeof window !== 'undefined'
          ? window.location.pathname + (window.location.search ? window.location.search : '')
          : undefined
      const res = await initiatePaymentWithReservation({
        reservation_data: baseReservationData,
        payment_method: 'card',
        initial_url: initialUrl || undefined,
      })
      if (!res) throw new Error('Empty response from payment initiation')
      setReservationId(res.reservation_id)
      if (res.requires_redirect && res.payment_url) {
        const inIframe = (() => {
          try { return window.self !== window.top } catch { return false }
        })()
        if (inIframe) {
          window.open(res.payment_url, '_blank', 'noopener,noreferrer')
          setSubmitted(true)
        } else {
          window.location.href = res.payment_url
        }
        return
      }
      setSubmitted(true)
    } else {
      const res = await createReservation({
        ...baseReservationData,
        payed: params.value.source === 'zlavadna' ? (state.couponVerified === true || state.isExternalPaid) : false,
      })
      if (!res) throw new Error('Empty response from reservation creation')
      setReservationId(res.id)
      setSubmitted(true)
    }
  } catch (e) {
    submitError.value = t(getReservationErrorKey(e))
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  let slugToLoad = params.value.facilitySlug?.trim() ?? ''
  if (slugToLoad) {
    resolvedSlugRef.value = ''
  } else {
    const serviceIdParam = params.value.serviceId?.trim() ?? null
    const branchIdParam = params.value.branchId?.trim() ?? null
    if (serviceIdParam) {
      try {
        const res = await getFacilitySlugByService(
          serviceIdParam,
          params.value.source ?? undefined
        )
        slugToLoad = res.slug ?? ''
      } catch {
        slugToLoad = ''
      }
    } else if (branchIdParam) {
      const branchId = Number(branchIdParam)
      if (Number.isFinite(branchId)) {
        try {
          const res = await getFacilitySlugByBranch(branchId)
          slugToLoad = res.slug ?? ''
        } catch {
          slugToLoad = ''
        }
      }
    }
    if (!slugToLoad) {
      notFound.value = true
      loading.value = false
      return
    }
    resolvedSlugRef.value = slugToLoad
  }

  try {
    const [facility, settings, pmRes] = await Promise.all([
      getFacility(slugToLoad),
      getPluginSettings(slugToLoad),
      getPaymentMethods(),
    ])
    if (!facility || !settings) {
      notFound.value = true
      loading.value = false
      return
    }
    if (!settings.active) {
      notFound.value = true
      loading.value = false
      return
    }
    facilityId.value = facility.id
    facilityName.value = facility.name ?? ''
    pluginSettings.value = settings
    const pmData = pmRes as { data?: PaymentMethodItem[] } | PaymentMethodItem[]
    paymentMethods.value = Array.isArray(pmData) ? pmData : (pmData.data ?? [])
    const source = params.value.source ?? 'plugin'
    const serviceIdParam = params.value.serviceId?.trim() ?? null
    if (source === 'zlavadna' && serviceIdParam) {
      try {
        const resolved = await resolveZlavaDnaService(serviceIdParam)
        const internalId = resolved.service_id ?? resolved.package_id ?? null
        const branchIdRaw =
          resolved.branch_id ??
          (Array.isArray(resolved.branch_ids) ? resolved.branch_ids[0] : null)
        if (internalId != null && branchIdRaw != null) {
          const [branch, products] = await Promise.all([
            getBranch(branchIdRaw),
            getServicesPackages(branchIdRaw, 'zlavadna'),
          ])
          const product = products.find((p) => p.id === internalId) ?? null
          if (branch && product) {
            setBranch(branch)
            setProduct(product)
            setWizardStep('datetime')
          }
        }
      } catch {
        // Resolve failed (e.g. invalid ZD id); leave step at branches
      }
    } else if (params.value.branchId) {
      const branchId = Number(params.value.branchId)
      if (!Number.isNaN(branchId)) {
        const branch = await getBranch(branchId)
        if (branch) {
          setBranch(branch)
          const serviceId = serviceIdParam ? Number(serviceIdParam) : NaN
          if (Number.isFinite(serviceId)) {
            const products = await getServicesPackages(
              branch.id,
              params.value.source ?? undefined
            )
            const product = products.find((p) => p.id === serviceId) ?? null
            if (product) {
              setProduct(product)
              setWizardStep('datetime')
            } else {
              setWizardStep('products')
            }
          } else {
            setWizardStep('products')
          }
        }
      }
    }
    const p = params.value
    if (
      p.name != null ||
      p.surname != null ||
      p.email != null ||
      p.phone != null ||
      (p.source === 'zlavadna' && (p.zdNumber != null || p.zdPassword != null))
    ) {
      setCustomer({
        name: p.name ?? '',
        surname: p.surname ?? '',
        email: p.email ?? '',
        phone: p.phone ?? '',
        ...(p.source === 'zlavadna'
          ? {
              couponCode: p.zdNumber ?? '',
              couponPassword: p.zdPassword ?? '',
            }
          : {}),
      })
    }
    if (p.source === 'zlavadna') {
      setZdDealItemId(p.serviceId ?? null)
      setExternalPricing(p.zdPrice ?? null, p.zdPaid ?? false)
      const zlavaMethod = paymentMethods.value.find((m) =>
        m.name.toLowerCase().includes('zlavadna')
      )
      if (zlavaMethod) setSelectedPaymentMethodId(zlavaMethod.id)
    }
    const cssUrl = params.value.css
    if (typeof cssUrl === 'string' && cssUrl.trim() !== '') {
      const trimmed = cssUrl.trim()
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = trimmed
        link.id = 'plugin-external-css'
        document.head.appendChild(link)
        externalCssLink.value = link
      }
    }
  } catch {
    notFound.value = true
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(() => {
  const link = externalCssLink.value ?? document.getElementById('plugin-external-css')
  if (link?.parentNode) link.parentNode.removeChild(link)
  externalCssLink.value = null
})

watch(
  () => params.value.facilitySlug,
  () => resetWizard()
)

watch(
  () => [state.step, hasPaymentOptions.value, paymentMethods.value, pluginSettings.value?.enabled_payment_methods, state.selectedPaymentMethodId] as const,
  ([step, hasOptions, methods]) => {
    if (step !== 'summary' || !hasOptions || state.selectedPaymentMethodId != null) return
    const enabledIds = pluginSettings.value?.enabled_payment_methods ?? []
    const first = enabledIds.length > 0
      ? methods.find((m) => enabledIds.includes(m.id)) ?? methods[0]
      : methods[0]
    if (first) setSelectedPaymentMethodId(first.id)
  },
  { immediate: true }
)
</script>
